<template>
  <ValidationObserver ref='validator' v-slot='{ handleSubmit }' slim>
    <div class='row'>
      <div class='col-xs-12 col-sm-12 col-md-6'>
        <text-box v-model='eanCode' :label="$t('identification.smartMeter.eanCode')"
          :label-tooltip="$t('chargingStationDeclaration.identification.eanTooltip')"
          :rules="{ 'ean-code-validator': true, required: true }" placeholder='541447856985698523'></text-box>
        <div class='lib__field__note'>
          <a v-if='!isIdentifiedAndCanContinue' href='#' @click='whereFoundEanModalOpen = true'>{{
            $t('ean.whereFoundEan.title') }}</a>
        </div>
      </div>
      <div class="col-xs-12 col-sm-12 col-md-6">
        <text-box :label="$t('prosumerDeclaration.identification.meterLabel')" :max="4" :min="4" onlyNumbers required
          v-model="meterLastFourNumbers"></text-box>
        <div class="lib__field__note">
          <a v-if='!isIdentifiedAndCanContinue' href="#" @click="whereFindMeterLastDigitsOpen = true">
            {{ $t('prosumerDeclaration.verifyMeter.help') }}
          </a>
        </div>
      </div>
    </div>

    <template v-if='!isIdentifiedAndCanContinue'>
      <ModalDialog :closeable="$t('common.close')" :open.sync='whereFoundEanModalOpen' size='small'>
        <h3 v-html="$t('ean.whereFoundEan.title')"></h3>
        <p>
          <span v-html="$t('ean.whereFoundEan.invoice')"></span>
          <br>
          <span v-html="$t('ean.whereFoundEan.formOnline1')"></span>
          <button class='lib__no-margin lib__chat_trigger lib__btn lib__btn--primary lib__btn--small' @click='openChat'>
            <i class='fas fa-comments'></i></button>
          <br>
          <span v-html="$t('ean.whereFoundEan.formOnline2')"></span>
        </p>
      </ModalDialog>

      <FindMeterLastDigitsModalDialog v-model="whereFindMeterLastDigitsOpen" />

      <connection-point-area :check-for-elec='true' :check-for-gas='false' :hide-success-messages='false'
        :showSynergridLink='true' :sub-city='subCity' />

      <template v-if='!isIdentified'>
        <Recaptcha :site-key='siteKey' :key='recaptchaKey' v-model='recaptchaToken' :by-pass='$testMode' />

        <nav class='lib__btns-nav-list'>
          <button :class="{ 'lib__btn--loading': loading, 'lib__btn--disabled': loading }"
            class='lib__btn lib__btn--rounded lib__btn--primary' type='button' @click='handleSubmit(identify)'>
            {{ $t('identification.smartMeter.identify') }}
          </button>
        </nav>
      </template>
      <template v-if='hasError'>
        <Feedback icon level='danger' outline simple>
          <p v-html='$t(`prosumerDeclaration.identification.eanAndMeterNotFound`)'></p>
        </Feedback>
      </template>
    </template>

  </ValidationObserver>
</template>

<script lang='ts'>
import { Emit, Vue, Watch } from 'vue-property-decorator'
import ZipCodeLocalitySelector from '@/components/inputs/Address/ZipCodeLocalitySelector.vue'
import ConnectionPointArea from '@/components/inputs/Address/ConnectionPointArea.vue'
import FindMeterLastDigitsModalDialog from '@/components/display/FindMeterLastDigitsModalDialog.vue'
import CollapseInfo from '@/components/display/CollapseInfo.vue'
import Component from 'vue-class-component'
import {
  ChargingStationDeclarationGridAccessPoint,
  CheckEanForChargingStationDeclarationResponse,
  CheckEanSearch,
  SubCity
} from '@/models'
import { Mutation, State } from 'vuex-class'
import { chatService, helper } from '@/services'
import { AxiosResponse } from 'axios'

@Component({
  name: 'identification',
  components: {
    ZipCodeLocalitySelector,
    ConnectionPointArea,
    CollapseInfo,
    FindMeterLastDigitsModalDialog
  }
})
export default class Identification extends Vue {
  //#region [Data]
  @State('eanCode', { namespace: 'chargingStationDeclaration/identification' })
  public eanCodeState!: string | null
  @State('meterLastFourNumbers', { namespace: 'chargingStationDeclaration/identification' })
  public meterLastFourNumbersState!: string | null
  @State('requestId', { namespace: 'chargingStationDeclaration' })
  public requestId!: string
  @State('gridAccessPoint', { namespace: 'chargingStationDeclaration/identification' })
  public gridAccessPointState!: ChargingStationDeclarationGridAccessPoint

  public subCity: SubCity | null = null
  public eanCode: string | null = null

  public recaptchaToken: string | null = null

  public gridAccessPoint: ChargingStationDeclarationGridAccessPoint | null = null
  public loading: boolean = false
  public hasError: boolean = false
  public isIdentified: boolean = false
  public recaptchaKey: number = 0

  public meterLastFourNumbers: string | null = null
  public whereFoundEanModalOpen: boolean = false
  public whereFindMeterLastDigitsOpen: boolean = false
  //#endregion
  //#region [Mutation]
  @Mutation('STORE', { namespace: 'chargingStationDeclaration/identification' })
  public store!: (index: { eanCode: string | null, meterLastFourNumbers: string | null }) => void

  //#endregion
  @Mutation('STORE_GRID_ACCESS_POINT', { namespace: 'chargingStationDeclaration/identification' })
  public storeGridAccessPoint!: (index: ChargingStationDeclarationGridAccessPoint | null) => void
  @Mutation('CLEAR_API_ERRORS', { namespace: 'apiErrors' })
  private clearApiErrors!: () => void

  //#region [Computed]
  public get isIdentifiedAndCanContinue(): boolean {
    return this.isIdentified && !!this.gridAccessPoint
  }

  public get siteKey() {
    return process.env.VUE_APP_CAPTCHA_SITE_KEY
  }

  //#endregion

  //#region [Watch]
  @Watch('eanCode')
  @Emit('eanCode')
  public onEanCodeChange(value: string | null) {
    this.clearApiErrors()
    if (value && value === this.eanCodeState) {
      // User input is the same as last identified EAN
      this.isIdentified = true
      return value
    }
    this.isIdentified = false
    return value
  }

  @Watch('eanCodeState')
  public onEanCodeStateChange(value: string | null) {
    if (!value) {
      this.isIdentified = false
    }
  }

  @Watch('isIdentified')
  @Emit('isIdentified')
  public onIsIdentifiedChange(value: boolean): boolean | null {
    return value
  }

  @Watch('isIdentifiedAndCanContinue', { immediate: true })
  @Emit('canContinue')
  public isIdentifiedAndCanContinueChange(value: boolean): boolean | null {
    return value
  }

  @Watch('gridAccessPoint')
  @Emit('gridAccessPoint')
  public onGridAccessPointChange(value: ChargingStationDeclarationGridAccessPoint):
    ChargingStationDeclarationGridAccessPoint | null {
    return value
  }

  //#endregion

  //#region [Method]
  public mounted() {
    this.eanCode = helper.clone(this.eanCodeState)
    this.meterLastFourNumbers = helper.clone(this.meterLastFourNumbersState)
    this.gridAccessPoint = helper.clone(this.gridAccessPointState)
    this.isIdentified = !!this.gridAccessPointState
  }

  public async checkEanForChargingStationDeclaration(): Promise<CheckEanForChargingStationDeclarationResponse> {
    this.hasError = false;

    if (!this.recaptchaToken) {
      throw new Error('No recaptcha token found');
    }

    const response = await this.$api.get<CheckEanForChargingStationDeclarationResponse>(
      `api/assets/check-ean-meter/charging-station-declaration?ean=${this.eanCode!.trim()}&meterLastFourNumbers=${this.meterLastFourNumbers}`,
      {
        headers: {
          'g-recaptcha-response': this.recaptchaToken
        },
        cache: {
          cachePredicate: (status) => true,
        }
      }
    )

    if (response.status !== 200) {
      this.hasError = true
    }
    return response.data
  }

  public async identify() {
    if (this.eanCode) {
      try {
        this.loading = true

        const checkEanForChargingStationDeclarationResponse = await this.checkEanForChargingStationDeclaration()

        this.gridAccessPoint = checkEanForChargingStationDeclarationResponse.gridAccessPoint
        this.storeGridAccessPoint(this.gridAccessPoint)

        this.store({ eanCode: this.eanCode!.trim(), meterLastFourNumbers: this.meterLastFourNumbers })

        if (this.gridAccessPoint && checkEanForChargingStationDeclarationResponse.token) {
          sessionStorage.setItem('token', checkEanForChargingStationDeclarationResponse.token)
          this.isIdentified = true
        }

        this.loading = false

        if (!this.isIdentifiedAndCanContinue) {
          // Refresh the captcha for next identification to work
          this.recaptchaKey += 1
        }
      } catch (err) {
        this.loading = false
        this.isIdentified = false
        this.storeGridAccessPoint(null)
      }
    }
  }

  private openChat() {
    chatService.openChat()
  }

  //#endregion

  //#region [Emit]
  //#endregion
}
</script>
